// Tap a gig contact in the Next Gig Brief to reach them: call, text, WhatsApp or
// email, depending on what's saved. With nothing saved it points to Scene.
import { useNavigate } from "react-router-dom";
import { Phone, MessageSquare, MessageCircle, Mail, AtSign } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { openExternalLink } from "@/lib/utils";

// WhatsApp wants an international number with no "+" or leading zero. Fretless is
// UK-first, so a bare leading 0 is read as +44.
function whatsappNumber(phone) {
  const d = (phone || "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) return d.slice(1);
  if (d.startsWith("00")) return d.slice(2);
  if (d.startsWith("0")) return `44${d.slice(1)}`;
  return d;
}

export default function ContactSheet({ contact, onClose }) {
  const navigate = useNavigate();
  const phone = (contact?.phone || "").trim();
  const email = (contact?.email || "").trim();
  const insta = (contact?.instagram_handle || "").trim().replace(/^@/, "");
  const dial = phone.replace(/[^\d+]/g, "");
  const hasAnything = !!(phone || email || insta);

  // tel:, sms: and mailto: hand off to the phone/mail app; web links follow the PWA rule.
  const go = (url) => { window.location.href = url; };

  return (
    <Dialog open={!!contact} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent style={{ maxWidth: 360 }} data-testid="contact-sheet">
        <DialogHeader>
          <DialogTitle>{contact?.name}</DialogTitle>
          <DialogDescription>
            {contact?.instrument || (hasAnything ? "Gig contact" : "No contact details saved yet")}
          </DialogDescription>
        </DialogHeader>

        {phone && (
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" onClick={() => go(`tel:${dial}`)} data-testid="contact-call">
              <Phone className="h-4 w-4 mr-1.5" />Call
            </Button>
            <Button variant="outline" onClick={() => go(`sms:${dial}`)} data-testid="contact-text">
              <MessageSquare className="h-4 w-4 mr-1.5" />Text
            </Button>
            <Button variant="outline" onClick={() => openExternalLink(`https://wa.me/${whatsappNumber(phone)}`)} data-testid="contact-whatsapp">
              <MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp
            </Button>
          </div>
        )}
        {phone && <p className="text-xs text-muted-foreground -mt-1">{phone}</p>}

        {email && (
          <Button variant="outline" onClick={() => go(`mailto:${email}`)} data-testid="contact-email">
            <Mail className="h-4 w-4 mr-1.5" />{email}
          </Button>
        )}
        {insta && (
          <Button variant="outline" onClick={() => openExternalLink(`https://instagram.com/${insta}`)} data-testid="contact-instagram">
            <AtSign className="h-4 w-4 mr-1.5" />{insta}
          </Button>
        )}

        {!hasAnything && (
          <Button variant="outline" onClick={() => { onClose(); navigate("/scene"); }} data-testid="contact-add-details">
            Add details in Scene
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
